import { makeAutoObservable } from "mobx";
import { Bridge, Cookie, Platform } from "@xt/xtbase";
import { get_symbolStar } from "api/v4/account";
import { get_tokenUserKey } from "api/old/xt-lever";
import { get_privateUserVipInfo } from "api/old/xt-user";

export interface UserInfoProps {
  userAccountLevel?: number;
  fiatCurrencyIds?: number[];
}

interface CountryInfo {
  code?: number;
}

export interface AuthInfoProps {
  country?: CountryInfo;
}

export interface UserVipInfoProps {
  makerFeeRate: number;
  takerFeeRate: number;
  vipLevel: number;
  vipName: string;
}

interface StateProps {
  isLogin: boolean;
  token: string;
  userInfo: null | UserInfoProps;
  userVipInfo: WithUndefined<UserVipInfoProps>;
  symbolStar: string[];
  leverWsUserKey: string;
  authInfo: null | AuthInfoProps;
}

interface MainAppStateProps {
  loginStatus?: number;
  token?: string;
  userInfo?: null | UserInfoProps;
  authInfo?: null | AuthInfoProps;
}

const user = makeAutoObservable(
  {
    _mainAppState: null as null | MainAppStateProps,
    _userVipInfo: undefined as StateProps["userVipInfo"],
    _symbolStar: [] as StateProps["symbolStar"],
    _leverWsUserKey: "" as StateProps["leverWsUserKey"],

    bindMainState(payload: null | MainAppStateProps) {
      this._mainAppState = payload || null;
      const isLogin = !!(payload && (payload.loginStatus === 1 || payload.authInfo || payload.userInfo));
      if (!isLogin) {
        this._userVipInfo = undefined;
        this._symbolStar = [];
        this._leverWsUserKey = "";
      }
    },

    get isLogin(): boolean {
      const state = this._mainAppState;
      if (state) {
        return state.loginStatus === 1 || !!(state.authInfo || state.userInfo);
      }
      return !!this.token;
    },

    get token(): string {
      return ((Platform.isInApp() ? Bridge.getToken() : null) || Cookie.get("token") || this._mainAppState?.token || "") as string;
    },

    get userInfo(): null | UserInfoProps {
      return this._mainAppState?.userInfo || null;
    },

    get userVipInfo(): WithUndefined<UserVipInfoProps> {
      return this._userVipInfo;
    },

    get symbolStar(): string[] {
      return this._symbolStar;
    },

    get leverWsUserKey(): string {
      return this._leverWsUserKey;
    },

    get authInfo(): null | AuthInfoProps {
      return this._mainAppState?.authInfo || null;
    },

    get isSubAcc(): boolean {
      return !!(this.userInfo && this.userInfo.userAccountLevel === 2);
    },

    updateState(payload: Partial<StateProps>) {
      const nextMainState = this._mainAppState ? { ...this._mainAppState } : {};
      let hasMainStatePatch = false;

      if ("isLogin" in payload) {
        nextMainState.loginStatus = payload.isLogin ? 1 : 2;
        hasMainStatePatch = true;
      }
      if ("token" in payload) {
        nextMainState.token = payload.token;
        hasMainStatePatch = true;
      }
      if ("userInfo" in payload) {
        nextMainState.userInfo = payload.userInfo;
        hasMainStatePatch = true;
      }
      if ("authInfo" in payload) {
        nextMainState.authInfo = payload.authInfo;
        hasMainStatePatch = true;
      }
      if (hasMainStatePatch) {
        this._mainAppState = nextMainState;
      }

      if ("userVipInfo" in payload) {
        this._userVipInfo = payload.userVipInfo;
      }
      if ("symbolStar" in payload) {
        this._symbolStar = payload.symbolStar || [];
      }
      if ("leverWsUserKey" in payload) {
        this._leverWsUserKey = payload.leverWsUserKey || "";
      }
    },

    getUserVipInfo() {
      get_privateUserVipInfo().then((data) => (this._userVipInfo = data));
    },

    getSymbolStar() {
      return get_symbolStar().then((ary) => (this._symbolStar = ary));
    },

    getTokenUserKey() {
      get_tokenUserKey().then((data) => (this._leverWsUserKey = data));
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default user;
